import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import CheckBox from '@/components/basicComponents/checkbox';
import { EllipsisWithTooltip } from '@/components/basicComponents/ellipsisWithTooltip';
import Input from '@/components/basicComponents/input';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Textarea from '@/components/basicComponents/textarea';
import { Investment, IOfferingMaterialsAttachments } from '@/components/dealCatalog-page/types';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { GET_LEGAL_ENTITY_DOCUMENTATION_QUERY } from '@/components/investors-page/investorDetailsPage/tabs/documentationTab/queries';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { useGoBack } from '@/hooks/useGoBack';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { gql, useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  forwardDealDocumentsInputSchema,
  forwardDealDocumentsResponseSchema,
  sendDealDocumentsToInvestorInputSchema,
  sendDealDocumentsToInvestorResponseSchema
} from '@trace-8/trace8-schema';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { z } from 'zod/v4';
import { EXECUTE_ACTION_MUTATION, SEND_DEAL_DOCUMENTS_EMAIL_MUTATION } from '../queries';
import { IExecuteActionMutationInput, IExecuteActionMutationResponse } from '../types';

const GET_ALLOCATION_QUERY = gql(`
  query ForwardDealDocumentsPageAllocation($id: String!) {
    Allocation(id: $id) {
      id
      legalEntity {
        id
      }
    }
  }
`);

const INVESTMENT_QUERY = gql(`
  query ForwardDealDocumentsPageInvestment($id: String!) {
    Investment(id: $id) {
      id
      name
      offeringMaterialsAttachments {
        id
        asset {
          url
          assetKey
        }
        visibility
      }
    }
  }
`);

type ForwardDealDocumentsInput = z.infer<typeof forwardDealDocumentsInputSchema>;
type ForwardDealDocumentsResponse = z.infer<typeof forwardDealDocumentsResponseSchema>;
type SendDealDocumentsToInvestorInput = z.infer<typeof sendDealDocumentsToInvestorInputSchema>;
type SendDealDocumentsToInvestorResponse = z.infer<typeof sendDealDocumentsToInvestorResponseSchema>;

const defaultMessageToInvestor =
  'Please find the attached materials for your review. The "Download" button will open a new page allowing you to download all available documents. Your Advisor will reach out to you shortly with the appropriate paperwork to complete your investment.';

const defaultFormValues: ForwardDealDocumentsInput = {
  email: '',
  subject: '',
  investmentId: '',
  messageToInvestor: defaultMessageToInvestor,
  selectedDocumentIds: []
};

export const ForwardDealDocuments = () => {
  const goBack = useGoBack();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();

  const [searchParams, setSearchParams] = useSearchParams();

  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    handleSubmit
  } = useForm<ForwardDealDocumentsInput>({
    defaultValues: defaultFormValues,
    resolver: zodResolver(forwardDealDocumentsInputSchema)
  });

  const allocationId = location.state?.allocationId;
  const forwardToInvestor = location.state?.forwardToInvestor;
  const backToTitle = searchParams.get('backToTitle') || '';

  const [legalEntityId, setLegalEntityId] = useState('');
  const [attachments, setAttachments] = useState<IOfferingMaterialsAttachments[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const [executeAction, { loading: executeActionLoading }] = useMutation<
    IExecuteActionMutationResponse<ForwardDealDocumentsInput, ForwardDealDocumentsResponse>,
    IExecuteActionMutationInput<ForwardDealDocumentsInput>
  >(EXECUTE_ACTION_MUTATION, {
    onError: (error) => {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    },
    onCompleted: () => {
      setShowSuccessDialog(true);
    }
  });

  const [sendDealDocumentsEmail, { loading: sendDealDocumentsEmailLoading }] = useMutation(SEND_DEAL_DOCUMENTS_EMAIL_MUTATION, {
    onError: (error) => {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    },
    onCompleted: () => {
      setShowSuccessDialog(true);
    }
  });

  useEffect(() => {
    if (!params?.strategyId) return;
    setValue('investmentId', params.strategyId);
  }, [params?.strategyId]);

  const { loading: allocationLoading } = useQuery<{
    Allocation: { id: string; legalEntity: { id: string } };
  }>(GET_ALLOCATION_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !allocationId,
    variables: { id: allocationId },
    onCompleted: (data) => {
      const { legalEntity } = data?.Allocation ?? {};

      setLegalEntityId(legalEntity?.id ?? '');
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { data: investmentData, loading: investmentLoading } = useQuery<{ Investment: Investment }>(INVESTMENT_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !params?.strategyId,
    variables: { id: params?.strategyId },
    onCompleted: async (data) => {
      const { name, offeringMaterialsAttachments } = data?.Investment ?? {};

      setValue('subject', name);
      setAttachments(
        offeringMaterialsAttachments.sort((a, b) => {
          const aName = a?.asset?.assetKey?.split('/')?.pop() ?? '';
          const bName = b?.asset?.assetKey?.split('/')?.pop() ?? '';
          return aName.localeCompare(bName);
        }) ?? []
      );
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { loading: getLegalEntityDocumentationLoading } = useQuery(GET_LEGAL_ENTITY_DOCUMENTATION_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip: !legalEntityId,
    variables: {
      id: legalEntityId
    },
    onCompleted: (data) => {
      if (!data.getLegalEntityDocumentation) return;
      setValue('email', data?.getLegalEntityDocumentation?.registeredAddress?.email ?? '');
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const openStrategyDetailsPage = () => {
    navigate(`/strategyDetails/${params?.strategyId}`);
  };

  const onSubmit = (data: ForwardDealDocumentsInput) => {
    setErrorMessage(null);

    if (allocationId) {
      if (forwardToInvestor) {
        executeAction({
          variables: {
            data: {
              allocationId: allocationId,
              action: 'sendDealDocumentsToInvestor',
              actionDataPayload: data as SendDealDocumentsToInvestorInput
            }
          }
        });
      } else {
        executeAction({
          variables: {
            data: {
              allocationId: allocationId,
              action: 'forwardDealDocuments',
              actionDataPayload: data as ForwardDealDocumentsInput
            }
          }
        });
      }

      return;
    }

    const { investmentId, ...sendDealDocumentsEmailData } = data;
    sendDealDocumentsEmail({
      variables: {
        data: sendDealDocumentsEmailData
      }
    });
  };

  if (allocationLoading || investmentLoading || getLegalEntityDocumentationLoading) return <LoaderOnWholeScreen />;

  return (
    <>
      {showSuccessDialog && (
        <ModalWrappedContent
          isOpen={showSuccessDialog}
          onClose={() => goBack({ fallBack: '/transactions' })}
          showCancelButton={false}
          isUseOutsideClick={false}
          confirmButton={{ name: 'OK', onClick: () => goBack({ fallBack: '/transactions' }) }}
        >
          <ModalBody>
            <ApprovedIcon width={60} height={60} />
            <ModalTitle>Deal Documents Sent</ModalTitle>
            <ModalDescription>Sent to: {getValues('email')}</ModalDescription>
          </ModalBody>
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={() => goBack({ fallBack: '/transactions' })} backToTitle={backToTitle} />} />
        <PageTitle title={investmentData?.Investment?.name ?? ''} />
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <PaddingWrap>
          <ForwardDealDocumentsForm isMobile={isMobile} id="forwardDealDocumentsForm" onSubmit={handleSubmit(onSubmit, () => window.scrollTo(0, 0))}>
            <SubTitle>Forward Deal Documents</SubTitle>
            <HelperText>Select all documents to send to the investor.</HelperText>
            <ForwardDealDocumentsFieldsWrapper>
              <div>
                <RedirectText onClick={openStrategyDetailsPage}>{investmentData?.Investment?.name ?? ''}</RedirectText>
                <Controller
                  name="selectedDocumentIds"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      {attachments.map((attachment) => (
                        <React.Fragment key={attachment.id}>
                          <CheckboxItem
                            id={attachment.id}
                            onClick={() => {
                              const updatedEligibility = value.includes(attachment.id)
                                ? value.filter((el) => el !== attachment.id)
                                : [...value, attachment.id];
                              onChange(updatedEligibility);
                            }}
                          >
                            <CustomCheckBox
                              id={attachment.id}
                              isActive={value.includes(attachment.id)}
                              checkBoxIconSizes={{ width: 12, height: 9.32 }}
                            />
                            <EllipsisWithTooltip maxContentWidth={680} title={attachment?.asset?.assetKey?.split('/')?.pop()}>
                              {attachment?.asset?.assetKey?.split('/')?.pop()}
                            </EllipsisWithTooltip>
                          </CheckboxItem>
                        </React.Fragment>
                      ))}
                      {errors?.selectedDocumentIds?.message && <ErrorText>{errors.selectedDocumentIds.message}</ErrorText>}
                    </>
                  )}
                />
              </div>
              <TwoColumnsWrapper isMobile={isMobile}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input label="Email" value={value} onChange={onChange} errorText={error?.message} isRequiredField />
                  )}
                />
                <Controller
                  name="subject"
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input label="Subject" value={value} onChange={onChange} errorText={error?.message} isRequiredField />
                  )}
                />
              </TwoColumnsWrapper>
              <Controller
                name="messageToInvestor"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Textarea
                    label="Message to Investor"
                    value={value}
                    onChange={onChange}
                    placeholder="Enter optional message"
                    size="lg"
                    errorText={error?.message}
                  />
                )}
              />
            </ForwardDealDocumentsFieldsWrapper>
            <ButtonsWrapper>
              <Button onClick={() => goBack({ fallBack: '/transactions' })} type="reset" styleType="outline" size="lg">
                Cancel
              </Button>
              <Button
                size="lg"
                styleType={executeActionLoading || sendDealDocumentsEmailLoading ? 'disabled' : 'default'}
                isLoading={executeActionLoading || sendDealDocumentsEmailLoading}
              >
                Send
              </Button>
            </ButtonsWrapper>
          </ForwardDealDocumentsForm>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const ForwardDealDocumentsForm = styled.form<{ isMobile: boolean }>`
  margin: 50px 0;
  padding: ${({ isMobile }) => (isMobile ? '20px 30px' : '40px')};
  background: ${themeVar('layer.1')};
  border: 1px solid ${themeVar('border.base')};
  border-radius: 10px;
`;

const SubTitle = styled.p`
  font-size: 20px;
  font-weight: 700;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
`;

const HelperText = styled.p`
  padding: 10px 0px;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  color: ${themeVar('font.weak')};
`;

const RedirectText = styled.p`
  font-size: 16px;
  font-weight: 600;
  line-height: 22.4px;
  cursor: pointer;
  color: ${themeVar('font.action')};
`;

const ForwardDealDocumentsFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 40px;
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  color: ${themeVar('font.base')};
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  cursor: pointer;
  padding: 6px;
`;

const CustomCheckBox = styled(CheckBox)`
  width: 20px;
  min-width: 20px;
  height: 20px;
`;

const TwoColumnsWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '1fr 1fr')};
  gap: 10px;
`;

const ErrorText = styled.p`
  color: ${themeVar('context.error')};
  font-family: Blinker;
  font-weight: 400;
  font-size: 16px;
  line-height: 22.4px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const ModalTitle = styled.h2`
  text-align: center;
  font-size: 33px;
  font-weight: 400;
  line-height: 46.2px;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  text-align: center;
  color: ${themeVar('font.base')};
`;
